import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CompanyFundsControlsProps {
  currentPage: number;
  totalPages: number;
  pageInputValue: string;
  onPrev: () => void;
  onNext: () => void;
  onPageInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPageInputBlur: () => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: string) => void;
  totalCount: number;
  isLoading: boolean;
}

function formatTotalCount(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k+`;
  return count.toString();
}

export default function CompanyFundsControls(props: CompanyFundsControlsProps) {
  const {
    currentPage,
    totalPages,
    pageInputValue,
    onPrev,
    onNext,
    onPageInputChange,
    onPageInputBlur,
    rowsPerPage,
    onRowsPerPageChange,
    totalCount,
    isLoading,
  } = props;

  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1.5">
          <Input
            className="h-8 w-12"
            type="number"
            min={1}
            max={totalPages}
            value={pageInputValue}
            onChange={onPageInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onPageInputBlur();
            }}
            onBlur={onPageInputBlur}
          />
          <span className="text-sm text-muted-foreground">/</span>
          <span className="text-sm text-muted-foreground">{totalPages}</span>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Select value={String(rowsPerPage)} onValueChange={onRowsPerPageChange}>
          <SelectTrigger className="h-8 w-[110px]">
            <SelectValue placeholder={String(rowsPerPage)} />
          </SelectTrigger>
          <SelectContent side="top">
            <SelectItem value="10">10 rows</SelectItem>
            <SelectItem value="100">100 rows</SelectItem>
            <SelectItem value="500">500 rows</SelectItem>
          </SelectContent>
        </Select>

        <div className="text-sm text-muted-foreground flex items-center">
          <span>
            {formatTotalCount(totalCount)} records
            {totalCount > rowsPerPage * totalPages ? ' (estimated)' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}
